import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { motion } from "framer-motion";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard/profile');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await API.post(`${process.env.REACT_APP_API_URL}/api/login`, { email, password });
      login(response.data.token);
      navigate('/dashboard/');
    } catch (error) {
      toast.error(error.response.data.message, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light'
      });
      console.error('Login failed', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.1 }
    },
    exit: { opacity: 0, transition: { duration: 0.4, ease: "easeIn" } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

        .login-root {
          min-height: 100vh;
          background: #0a0a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Poppins', sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* Ambient light blobs */
        .blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          opacity: 0.18;
          pointer-events: none;
        }
        .blob-1 {
          width: 420px; height: 420px;
          background: radial-gradient(circle, #6c63ff, #a855f7);
          top: -80px; left: -100px;
          animation: drift1 10s ease-in-out infinite alternate;
        }
        .blob-2 {
          width: 340px; height: 340px;
          background: radial-gradient(circle, #06b6d4, #3b82f6);
          bottom: -60px; right: -80px;
          animation: drift2 12s ease-in-out infinite alternate;
        }
        .blob-3 {
          width: 200px; height: 200px;
          background: radial-gradient(circle, #f472b6, #ec4899);
          top: 50%; left: 60%;
          animation: drift3 8s ease-in-out infinite alternate;
        }

        @keyframes drift1 { from { transform: translate(0,0) scale(1); } to { transform: translate(40px, 30px) scale(1.08); } }
        @keyframes drift2 { from { transform: translate(0,0) scale(1); } to { transform: translate(-30px, -40px) scale(1.05); } }
        @keyframes drift3 { from { transform: translate(0,0) scale(1); } to { transform: translate(-20px, 20px) scale(0.9); } }

        /* Grid texture overlay */
        .grid-overlay {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
        }

        /* Card */
        .login-card {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 420px;
          margin: 24px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 48px 44px;
          backdrop-filter: blur(20px);
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.04) inset,
            0 40px 80px rgba(0,0,0,0.6),
            0 0 60px rgba(108, 99, 255, 0.06);
        }

        /* Brand / Logo area */
        .login-brand {
          text-align: center;
          margin-bottom: 36px;
        }
        .login-logo {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 52px; height: 52px;
          border-radius: 14px;
          background: linear-gradient(135deg, #6c63ff, #a855f7);
          margin-bottom: 20px;
          box-shadow: 0 8px 24px rgba(108, 99, 255, 0.35);
        }
        .login-logo svg {
          width: 26px; height: 26px;
          color: white;
        }
        .login-title {
          font-family: 'Poppins', sans-serif;
          font-size: 2.2rem;
          font-weight: 500;
          color: #f0f0f8;
          letter-spacing: -0.02em;
          line-height: 1.1;
          margin: 0 0 6px;
        }
        .login-subtitle {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.6);
          font-weight: 300;
          letter-spacing: 0.02em;
        }

        /* Divider */
        .divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 28px;
        }
        .divider-line {
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.07);
        }
        .divider-text {
          font-size: 0.72rem;
          color: rgba(255,255,255,0.46);
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        /* Field group */
        .field-group {
          margin-bottom: 16px;
          position: relative;
        }
        .field-label {
          display: block;
          font-size: 0.72rem;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.6);
          margin-bottom: 8px;
          transition: color 0.2s;
        }
        .field-group.focused .field-label {
          color: #a78bfa;
        }
        .field-wrapper {
          position: relative;
        }
        .field-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px; height: 16px;
          color: rgba(255,255,255,0.46);
          transition: color 0.2s;
          pointer-events: none;
        }
        .field-group.focused .field-icon {
          color: #a78bfa;
        }
        .field-input {
          width: 100%;
          padding: 13px 14px 13px 40px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          color: #f0f0f8;
          font-size: 0.9rem;
          font-family: 'Poppins', sans-serif;
          font-weight: 400;
          outline: none;
          transition: all 0.25s ease;
          box-sizing: border-box;
        }
        .field-input::placeholder { color: rgba(255,255,255,0.42); }
        .field-input:focus {
          background: rgba(167, 139, 250, 0.06);
          border-color: rgba(167, 139, 250, 0.4);
          box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.08);
        }

        /* Forgot password */
        .forgot-link {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
          margin-bottom: 28px;
        }
        .forgot-link a {
          font-size: 0.78rem;
          color: rgba(255,255,255,0.6);
          text-decoration: none;
          transition: color 0.2s;
          letter-spacing: 0.01em;
        }
        .forgot-link a:hover { color: #a78bfa; }

        /* Submit button */
        .submit-btn {
          width: 100%;
          padding: 14px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          font-size: 0.9rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          color: white;
          background: linear-gradient(135deg, #6c63ff 0%, #a855f7 100%);
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(108, 99, 255, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .submit-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.12), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .submit-btn:hover:not(:disabled)::before { opacity: 1; }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(108, 99, 255, 0.45);
        }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        /* Spinner */
        .spinner {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Sign up link */
        .signup-row {
          text-align: center;
          margin-top: 24px;
          font-size: 0.82rem;
          color: rgba(255,255,255,0.52);
        }
        .signup-row a {
          color: #a78bfa;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }
        .signup-row a:hover { color: #c4b5fd; }

        @media (max-width: 480px) {
          .login-card { padding: 32px 24px; margin: 16px; }
          .login-title { font-size: 1.8rem; }
        }
      `}</style>

      <motion.div
        className="login-root"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Background elements */}
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
        <div className="grid-overlay" />

        {/* Card */}
        <motion.div className="login-card" variants={itemVariants}>

          {/* Brand */}
          <motion.div className="login-brand" variants={itemVariants}>
            <div className="login-logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h1 className="login-title">Welcome back</h1>
            <p className="login-subtitle">Sign in to continue your journey</p>
          </motion.div>

          <motion.div className="divider" variants={itemVariants}>
            <div className="divider-line" />
            <span className="divider-text">credentials</span>
            <div className="divider-line" />
          </motion.div>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <motion.div
              className={`field-group ${focusedField === 'email' ? 'focused' : ''}`}
              variants={itemVariants}
            >
              <label className="field-label">Email address</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="field-input"
                  required
                />
              </div>
            </motion.div>

            {/* Password */}
            <motion.div
              className={`field-group ${focusedField === 'password' ? 'focused' : ''}`}
              variants={itemVariants}
            >
              <label className="field-label">Password</label>
              <div className="field-wrapper">
                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="field-input"
                  required
                />
              </div>
            </motion.div>

            {/* Forgot password */}
            <motion.div className="forgot-link" variants={itemVariants}>
              <Link to="/forgot-password">Forgot password?</Link>
            </motion.div>

            {/* Submit */}
            <motion.button
              type="submit"
              className="submit-btn"
              disabled={loading}
              variants={itemVariants}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <><div className="spinner" /><span>Signing in...</span></>
              ) : (
                <>
                  <span>Sign in</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </motion.button>

            {/* Signup link */}
            <motion.div className="signup-row" variants={itemVariants}>
              Don't have an account?{' '}
              <Link to="/signup">Create one</Link>
            </motion.div>
          </form>
        </motion.div>
      </motion.div>

      <ToastContainer />
    </>
  );
};

export default Login;